import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { formatMoney } from "./pipelineHelpers";

export default function ArPaymentInvoicePrint({ invoice, currency = "PHP" }) {
  const { activeClinic } = useAuth();
  const [letterhead, setLetterhead] = useState({ logo_url: null, address: null });

  const clinicId = activeClinic?.clinic_id || activeClinic?.id || null;
  const clinicName = activeClinic?.clinic_name || "Dental clinic";

  useEffect(() => {
    if (!clinicId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("clinic_settings")
        .select("logo_url, address")
        .eq("clinic_id", clinicId)
        .maybeSingle();
      if (!cancelled) setLetterhead(data ?? {});
    })();
    return () => { cancelled = true; };
  }, [clinicId]);

  if (!invoice || typeof document === "undefined") return null;

  const payment = invoice.payment || {};
  const balance = invoice.balance || {};
  const paidAt = invoice.paidAt ? new Date(invoice.paidAt) : new Date();
  const paymentId = invoice.paymentEventId || "";
  const invoiceNumber = paymentId ? `INV-${paymentId.slice(0, 8).toUpperCase()}` : "AR-INVOICE";
  const amountPaid = Number(payment.amount_centavos || 0);
  const remaining = Number(balance.balance_centavos ?? balance.remaining_centavos ?? 0);

  const node = (
    <div className="dos-ar-invoice-portal">
      <style>{`
        .dos-ar-invoice-portal { display: none; }
        @media print {
          body > *:not(.dos-ar-invoice-portal) { display: none !important; }
          .dos-ar-invoice-portal { display: block !important; }
          html, body { background: #fff !important; }
          @page { margin: 12mm; size: A4; }
          .dos-ar-invoice {
            width: 100%;
            max-width: 720px;
            margin: 0 auto;
            padding: 0 8mm;
            font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            font-size: 11px;
            line-height: 1.45;
          }
          .dos-ar-invoice header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1.5pt solid #cbd5e1; padding-bottom: 8px; margin-bottom: 14px; }
          .dos-ar-invoice h1 { font-size: 16px; margin: 0; font-weight: 700; }
          .dos-ar-invoice .clinic-addr { font-size: 10px; color: #64748b; margin-top: 2px; white-space: pre-wrap; }
          .dos-ar-invoice .meta { text-align: right; }
          .dos-ar-invoice .label { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; }
          .dos-ar-invoice .num { font-size: 13px; font-weight: 700; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; margin-top: 2px; }
          .dos-ar-invoice .when { color: #64748b; margin-top: 4px; font-size: 10px; }
          .dos-ar-invoice .pp { display: flex; gap: 28px; margin-bottom: 16px; }
          .dos-ar-invoice .name { font-weight: 600; margin-top: 2px; }
          .dos-ar-invoice .box { border: .75pt solid #e2e8f0; border-radius: 4px; padding: 10px; margin-bottom: 14px; }
          .dos-ar-invoice .row { display: flex; justify-content: space-between; gap: 16px; padding: 3px 0; }
          .dos-ar-invoice .total { border-top: 1.5pt solid #cbd5e1; margin-top: 5px; padding-top: 6px; font-weight: 700; font-size: 12px; }
          .dos-ar-invoice .balance { color: #b45309; font-weight: 700; }
          .dos-ar-invoice footer { text-align: center; font-size: 9px; color: #94a3b8; border-top: .75pt solid #e2e8f0; padding-top: 10px; margin-top: 18px; }
        }
      `}</style>

      <div className="dos-ar-invoice">
        <header>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {letterhead.logo_url ? (
              <img src={letterhead.logo_url} alt=""
                   style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 4 }} />
            ) : null}
            <div>
              <h1>{clinicName}</h1>
              {letterhead.address ? <p className="clinic-addr">{letterhead.address}</p> : null}
            </div>
          </div>
          <div className="meta">
            <p className="label">Invoice</p>
            <p className="num">{invoiceNumber}</p>
            <p className="when">{paidAt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
          </div>
        </header>

        <section className="pp">
          <div>
            <p className="label">Patient</p>
            <p className="name">{invoice.patient?.full_name || "Patient"}</p>
            {invoice.patient?.patient_number ? (
              <p style={{ color: "#64748b", fontSize: 10 }}>{invoice.patient.patient_number}</p>
            ) : null}
          </div>
          <div>
            <p className="label">Treatment plan</p>
            <p className="name">{invoice.planCode || "Treatment plan"}</p>
          </div>
        </section>

        <section className="box">
          <p className="label" style={{ marginBottom: 6 }}>AR payment</p>
          <div className="row">
            <span>Amount paid</span>
            <strong>{formatMoney(amountPaid, currency)}</strong>
          </div>
          <div className="row">
            <span>Payment method</span>
            <span>{formatMethod(payment.method)}</span>
          </div>
          {payment.reference_number ? (
            <div className="row">
              <span>Reference / transaction #</span>
              <span>{payment.reference_number}</span>
            </div>
          ) : null}
          {payment.proof_url ? (
            <div className="row">
              <span>Payment proof</span>
              <span>Attached</span>
            </div>
          ) : null}
          {payment.notes ? (
            <div className="row">
              <span>Notes</span>
              <span>{payment.notes}</span>
            </div>
          ) : null}
          <div className="row total">
            <span>Remaining AR balance</span>
            <span className={remaining > 0 ? "balance" : ""}>{formatMoney(remaining, currency)}</span>
          </div>
        </section>

        <footer>
          This Dental OS invoice is not a BIR Official Receipt. Official receipt issuance remains the clinic's responsibility.
        </footer>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

function formatMethod(method) {
  return (method || "Payment").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
